import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Dumbbell, Euro, MapPin, Video, X } from "lucide-react";
import { formatDate, formatTime, getCurrencyIcon } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getRequestOptions } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookingClass, Booking, ClassWithClients } from "@/data/models";
import { toast } from "@/hooks/use-toast";

interface ClassCardProps {
  classItem: BookingClass;
  onEdit?: (classItem: BookingClass) => void;
  onBooking?: (classItem: BookingClass) => void;
}

const ClassCard = ({ classItem, onEdit: handleEditClick, onBooking: handleBookingClick }: ClassCardProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showClients, setShowClients] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [isClassFullyBooked, setIsClassFullyBooked] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientToRemove, setClientToRemove] = useState<{ id: string; name: string; } | null>(null);
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string; history: any[] } | null>(null);

  const handleRemoveClient = async () => {
    try {
      let fetchURL = `${import.meta.env.VITE_BOOKINGS_ENDPOINT}` + '/cancel';
      const booking = classItem.bookings.find(
        (booking) => booking.clientId === clientToRemove.id
      );
      const id = booking ? booking.id : null; // Get the ID if found, otherwise null
      const classId = classItem.id;
      const payload = { id, classId };
      const response = await fetch(fetchURL, getRequestOptions("POST", JSON.stringify(payload), "json"));
      
      if (!response.ok) {
        throw new Error("Failed to update the booking");
      }

      await response.json();

      setClients(prevClients => {
        const newClients = prevClients.filter(c => c.id !== clientToRemove.id);
        if (classItem.enrolled) {
          classItem.enrolled = newClients.length;
        }
        return newClients;
      });

      setClientToRemove(null);

      toast({
        title: t('clientDashboard.cancelSuccess.title'),
        description: t('clientDashboard.cancelSuccess.description'),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
    } catch (error) {
      console.error("Error adding class:", error);
    }
  };

  const handleClientClick = async (client: { id: string; firstName: string; lastName: string }) => {

    try {
      const response = await fetch(`${import.meta.env.VITE_CLIENT_CLASSES_ENDPOINT}` + "/" + client.id, getRequestOptions());

      if (!response.ok) {
        throw new Error('Failed to fetch classes');
      }

      const history = await response.json(); // Parse JSON data
      setSelectedClient({
        id: client.id,
        name: `${client.firstName} ${client.lastName}`,
        history
      });

    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const cancelClientBooking = async (client: any) => {
    setClientToRemove({
      id: client.id,
      name: `${client.firstName} ${client.lastName}`,
    });
  }

  useEffect(() => {
    setIsClassFullyBooked(Math.max(0, classItem.capacity - classItem.enrolled) === 0);
    if (classItem.bookings) {
      const bookings = classItem.bookings
        .map((booking: Booking) => booking.bookingClient)
        .filter(
          (client, index, self) =>
            client !== null && // Exclude null bookingClient
            index === self.findIndex((c) => c?.id === client?.id) // Ensure unique non-null clients
        );
      setClients(bookings);
    } else {
      setClients([]); // Clear the clients if bookings is null
      setIsBooked(classItem.isBooked);
    }
  }, [classItem]);

  return (
    <>
      <Card key={classItem.id} className="hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle className="text-xl font-semibold">
              {classItem.title}
            </CardTitle>
            <div className="flex items-center">
              {classItem.location === "Online" ? (
                <>
                  <Video className="mr-2 h-4 w-4 text-red-800" />
                  <span className="px-2 py-1 rounded-full text-sm bg-red-100 text-red-800">
                    {t('classes.online')}
                  </span>
                </>
              ) : (
                <>
                  <MapPin className="mr-2 h-4 w-4 text-blue-800" />
                  <span className="px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                    {classItem.location}
                  </span>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center text-gray-600">
              <Calendar className="w-5 h-5 mr-2" />
              <span>{formatDate(classItem.classDateTime)}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Clock className="w-5 h-5 mr-2" />
              <span>{formatTime(classItem.classDateTime)} ({classItem.duration} {t('bookingSummary.min')})</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Dumbbell className="w-5 h-5 mr-2" />
              <span>{classItem.instructor}</span>
            </div>
            <div className="flex items-center text-gray-600">
              {getCurrencyIcon({ currencyCode: classItem.currency })}
              <span>{classItem.price}</span>
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-500">
                {handleEditClick || !handleBookingClick ? (
                  <button
                    onClick={() => setShowClients(true)}
                    className="text-primary hover:underline focus:outline-none"
                  >
                    {`${classItem.enrolled || 0}/${classItem.capacity} ${t('classCard.enrolled')}`}
                  </button>
                ) : (
                  `${Math.max(0, classItem.capacity - classItem.enrolled)} ${t('classes.spots')}`
                )}
              </span>
              {handleEditClick ? (
                <Button
                  className="bg-primary hover:bg-secondary"
                  onClick={() => handleEditClick(classItem)}
                >
                  {t('classCard.editClass')}
                </Button>
              ) : handleBookingClick ? (
                <Button
                  className={`bg-primary hover:bg-secondary ${(isBooked || isClassFullyBooked) ? 'bg-gray-400 cursor-not-allowed opacity-50' : ''}`}
                  onClick={() => handleBookingClick(classItem)}
                  disabled={(isBooked || isClassFullyBooked)}
                >
                  {isBooked ? t('classCard.booked') : (isClassFullyBooked ? t('classCard.classFullyBooked') : t('classCard.bookNow'))}
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showClients} onOpenChange={setShowClients}>
        <DialogContent className="sm:max-w-[425px] w-[95vw] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('classCard.enrolledClients')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[50vh] w-full rounded-md">
            <div className="p-4">
              {classItem.bookings && clients && clients.length > 0 ? (
                <div className="space-y-4">
                  {clients.map((client) => (
                    <div key={client.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <div
                        className="flex flex-col flex-grow"
                        onClick={() => handleClientClick(client)}
                      >
                        <span className="font-medium">{`${client.firstName} ${client.lastName}`}</span>
                        <span className="text-sm text-gray-500">{client.email}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          cancelClientBooking(client);
                        }}
                        className="h-8 w-8">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">{t('classCard.noEnrolledClients')}</p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!clientToRemove} onOpenChange={() => setClientToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('classCard.confirmRemoval')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('classCard.confirmRemovalDescription', { name: clientToRemove?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.close")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => clientToRemove && handleRemoveClient()}>
              {t('common.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="sm:max-w-[425px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>{t('classCard.clientHistory', { name: selectedClient?.name })}</DialogTitle>
            <DialogDescription>
              {t('classCard.lastBookings')}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[50vh] w-full rounded-md">
            <div className="space-y-4 p-4">
              {selectedClient?.history.map((bookingClass) => (
                <div key={selectedClient.id} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="font-medium">{bookingClass.title}</div>
                    <div className="flex items-center">
                      {bookingClass.location === "Online" ? (
                        <>
                          <Video className="mr-2 h-4 w-4 text-red-800" />
                          <span className="px-2 py-1 rounded-full text-sm bg-red-100 text-red-800">
                            {t('classes.online')}
                          </span>
                        </>
                      ) : (
                        <>
                          <MapPin className="mr-2 h-4 w-4 text-blue-800" />
                          <span className="px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                            {bookingClass.location}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    <div className="flex items-center mt-1">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(bookingClass.classDateTime)}
                    </div>
                    <div className="flex items-center mt-1">
                      <Clock className="w-4 h-4 mr-2" />
                      {formatTime(bookingClass.classDateTime)}
                    </div>
                  </div>
                  {/* Show status if it's "Canceled" */}
                  {bookingClass.bookings[0].status.trim().toLowerCase() === "canceled" && (
                    <div className="text-sm text-red-600 mt-2">
                      {t('common.canceled')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClassCard;